import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { phoenixLegalConsentsListQuery } from '@marriott/mi-legal-graphql';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dropdown } from '../../molecules/Dropdown';
import { SelectOption } from '../../molecules/Dropdown/Dropdown.types';
import { useAuthStore, useLegalGenericStore } from '../../store';
import { CountriesAndCountryCode } from './__mock__/LegalConsents.mock';
import { ConsentError, ConsentErrorType, ErrorBannerProps } from './ConsentsList/ConsentsList.types';
import { LegalConsents } from './LegalConsents';
import { LegalConsent, OnConsentChangeParams, PhoenixLegalConsentsListType, PreState } from './LegalConsents.types';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { Button, ButtonTypeVariation, Messages, Modal } from '@marriott/mi-ui-library';
import { LegalValidatorControls } from '../../molecules/LegalValidatorControls/LegalValidatorControls';
import { generateInvalidResponseError, isEmpty } from '../../utils/consents';
import { useConsentPreState } from '../../hooks/useConsentPreState';
import { buildPreStateFromConsents } from '../../utils/preStateStorage';

const initialConsentsState = {
  list: [] as LegalConsent[], // initial list of consents
  sectionTypes: {},
  error: { type: ConsentErrorType.NONE, message: null } as ConsentError,
  consentsLedger: {}, // or the relevant structure for `consentsLedger`
};

let urlLocale: null | string = null;
const defaultLocale = 'en-us';
let urlVersion: string = '';
const defaultVersion = 'v1';
if (typeof window !== 'undefined') {
  const extractedLocale = new URLSearchParams(window.location.search).get('locale');
  if (extractedLocale) {
    urlLocale = extractedLocale.toLowerCase();
  }

  const extractedVersion = new URLSearchParams(window.location.search).get('version');
  if (extractedVersion) {
    urlVersion = extractedVersion.toLowerCase();
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LegalConsentValidator = (_props: any) => {
  const { sessionId } = useAuthStore();
  // const sessionId = '1234';
  const { isAuthorMode, language } = useLegalGenericStore();
  const { preState, savePreState, clearPreState } = useConsentPreState();
  const [locale, setLocale] = useState<string | null>(null);
  const [version, setVersion] = useState<string>(defaultVersion);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Don't resolve again
    if (locale !== null) return;

    // URL locale has top priority
    if (urlLocale) {
      setLocale(urlLocale);
      return;
    }

    if (!language) return;

    if (language.toLowerCase() !== defaultLocale) {
      setLocale(language.toLowerCase());
    } else {
      setLocale(defaultLocale);
    }
  }, [language, locale]);

  const [consentsState, setConsentsState] = useState(initialConsentsState);
  const [selectedCountryCode, setSelectedCountryCode] = useState('KR');
  const [selectedOption, setSelectedOption] = useState('');

  const [selectedConsentControlsMap, setSelectedConsentControlsMap] = useState<Map<string, boolean>>(new Map());
  const customLoaderInput = (
    <div>
      Loading Consents.....
      <span className="m-spinner-m ml-0"></span>
    </div>
  );
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [showResultPage, setShowResultPage] = useState(false);
  const [getLegalConsentsUxl] = useLazyQuery<PhoenixLegalConsentsListType>(phoenixLegalConsentsListQuery);
  const getLegalConsents = () => {
    const uxlOptions: LazyQueryHookOptions = {
      variables: { locale, version },
      context: generateApolloClientHeadersForUXLCalls(sessionId, 'phoenixLegalConsentsListQuery', locale ?? undefined),
      fetchPolicy: 'cache-first',
    };
    return getLegalConsentsUxl(uxlOptions);
  };

  // const getLegalConsents = () => {
  //   const uxlOptions: LazyQueryHookOptions = {
  //     // variables: { locale, version },
  //     context: {
  //       // uri: 'https://marriott-sites2-qa.adobecqms.net/graphql/execute.json/legal-consents/Locale-specific',
  //       uri: 'https://marriott-sites2-qa.adobecqms.net/graphql/execute.json/legal-consents',
  //       // uri: '/graphql/execute.json/legal-consents/getCountryConsents',
  //       headers: {
  //         'x-request-id': `Phoenix-Legal-${sessionId}`,
  //       },
  //     },
  //     fetchPolicy: 'network-only',
  //   };
  //   return getLegalConsentsUxl(uxlOptions);
  // };

  const handleConsentChange = (params: OnConsentChangeParams) => {
    const { updatedConsentsState } = params;
    setConsentsState(prevState => ({
      ...prevState,
      ...updatedConsentsState,
    }));
  };

  const isV2Enabled = selectedConsentControlsMap.get('show-v2-consent') === true;
  useEffect(() => {
    const cleanedVersion = urlVersion?.trim() || defaultVersion;

    setVersion(prev => {
      const finalVersion = isV2Enabled ? 'v2' : cleanedVersion;
      if (prev === finalVersion) return prev;
      return finalVersion;
    });
  }, [urlVersion, isV2Enabled]);

  useEffect(() => {
    const getCountryOptionList = async () => {
      // const allConsents = mockLegalConsentsProps.data?.consentsSectionList.items[0].consentsList || [];
      const legalConsentsRep = await getLegalConsents();
      const respData = legalConsentsRep.data?.content?.legal?.legalConsentsByLocale?.aemData?.data;
      const allConsents = respData?.consentsSectionList.items[0]?.consentsList || [];
      const allOptions = allConsents.flatMap(consent => consent.options);
      const uniqueOptions = Array.from(new Set(allOptions));

      const optionsWithLabels = uniqueOptions.map(option => ({
        label: option,
        value: option,
      }));
      return optionsWithLabels;
    };

    const fetchOptions = async () => {
      const fetchedOptions = await getCountryOptionList();
      setSelectedOption(fetchedOptions[0]?.value ?? '');
      setOptions(fetchedOptions);
    };
    !isAuthorMode && locale && fetchOptions();
  }, [isAuthorMode, locale, version]); // Empty dependency array to ensure this runs only once on mount

  const { control } = useForm({
    defaultValues: {
      countryOrRegion: selectedCountryCode,
      consentOption: selectedOption,
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowResultPage(prev => !prev);
  };

  const toggleState = () => {
    setSelectedConsentControlsMap(prev => {
      const next = new Map(prev);
      next.delete('show-modal');
      return next;
    });
  };

  const isConsentControlSelected = (code: string) => selectedConsentControlsMap.get(code) === true;

  const handleCountryChange =
    (onChange: (event: React.SetStateAction<string>) => void) => (value: React.SetStateAction<string>) => {
      setConsentsState(initialConsentsState);
      onChange(value);
      setSelectedCountryCode(value);
    };
  const handleOptionChange =
    (onChange: (event: React.SetStateAction<string>) => void) => (value: React.SetStateAction<string>) => {
      setConsentsState(initialConsentsState);
      onChange(value);
      setSelectedOption(value);
    };

  let termsLinkBehaviour: 'accordion' | 'newTab' | 'default';

  if (isConsentControlSelected('link-behaviour-to-accordion')) {
    termsLinkBehaviour = 'accordion';
  } else if (isConsentControlSelected('link-behaviour-to-new-tab')) {
    termsLinkBehaviour = 'newTab';
  } else {
    termsLinkBehaviour = 'default';
  }

  return (
    <form onSubmit={handleSubmit} className="legal-form">
      <div className="container-fluid">
        <div className="row align-items-center">
          <Controller
            name="countryOrRegion"
            control={control}
            render={({ field }) => (
              <Dropdown
                label="Select Country"
                className={clsx('col-12 col-md-6 col-lg-3 mb-3 mb-md-3 mb-lg-0')}
                options={CountriesAndCountryCode}
                name="countryOrRegion"
                value={field.value}
                handleChange={handleCountryChange(field.onChange)}
                id={'country'}
              />
            )}
          />
          <Controller
            name="consentOption"
            control={control}
            render={({ field }) => (
              <Dropdown
                label="Select Option"
                className={clsx('col-12 col-md-6 col-lg-3 mb-3 mb-md-3 mb-lg-0')}
                options={options}
                name="consentOption"
                value={field.value}
                handleChange={handleOptionChange(field.onChange)}
                id={'option'}
              />
            )}
          />
          <LegalValidatorControls
            urlParamVersion={urlVersion}
            selectedConsentControlsMap={selectedConsentControlsMap}
            setSelectedConsentControlsMap={setSelectedConsentControlsMap}
            clearPreState={clearPreState}
            disableClearPreState={isEmpty(preState)}
          />
          <div className="col-12 col-md-6 col-lg-3 mb-2 mb-md-3 mb-lg-0 d-flex justify-content-end">
            <Button
              type={ButtonTypeVariation.Submit}
              className="m-button m-submit-button-legal ml-5"
              isDisabled={loading}
            >
              {showResultPage ? 'Back' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
      <div className="ctp-consents-container">
        {showResultPage && <ResultPage consentList={consentsState.list} savePreState={savePreState} />}

        {!isConsentControlSelected('show-modal') && !showResultPage && locale && (
          <>
            {consentsState.error.type === ConsentErrorType.INVALID_RESPONSE && (
              <Messages
                className="mt-3"
                messageType="error-sev1"
                messageText={generateInvalidResponseError().message ?? ''}
              />
            )}
            {isConsentControlSelected('show-error-banner') && (
              <ErrorBanner consentsState={consentsState} className="mt-5 px-2" />
            )}
            <div style={{ border: '1px solid grey', marginTop: '30px', borderRadius: '5px' }}>
              <LegalConsents
                sessionId={sessionId}
                countryCode={selectedCountryCode}
                locale={locale}
                version={version}
                option={selectedOption}
                isValidateErrors={isConsentControlSelected('validate-error')}
                consentsState={consentsState}
                customLoader={isConsentControlSelected('show-custom-loader') ? customLoaderInput : null}
                anchorBehavior={isConsentControlSelected('override-links') ? 'newTab' : 'default'}
                linkBehavior={termsLinkBehaviour}
                setConsentsState={setConsentsState}
                onConsentChange={handleConsentChange}
                isAuthorMode={isAuthorMode}
                onLoading={setLoading}
                preState={preState}
              />
            </div>
          </>
        )}

        {isConsentControlSelected('show-modal') && (
          <OBJConsentsModal onModalClose={toggleState}>
            {/* <div style={{ border: '1px solid grey', marginTop: '30px', borderRadius: '5px' }}> */}
            <>
              {isConsentControlSelected('show-error-banner') && (
                <ErrorBanner consentsState={consentsState} className={'px-5'} />
              )}
              <div>
                <div className="m-3">
                  <div
                    className="skeleton-loader"
                    style={{
                      // width: '393px',
                      height: '32px',
                      borderRadius: '20px',
                    }}
                  ></div>
                </div>
                <div className="m-3">
                  <div
                    className="skeleton-loader"
                    style={{
                      // width: '393px',
                      height: '32px',
                      borderRadius: '20px',
                    }}
                  ></div>
                </div>
                <div className="m-3">
                  <div
                    className="skeleton-loader"
                    style={{
                      // width: '393px',
                      height: '32px',
                      borderRadius: '20px',
                    }}
                  ></div>
                </div>
                <div className="m-3">
                  <div
                    className="skeleton-loader"
                    style={{
                      // width: '201px',
                      height: '16px',
                      borderRadius: '20px',
                    }}
                  ></div>
                </div>
              </div>
              {locale && (
                <LegalConsents
                  sessionId={sessionId}
                  countryCode={selectedCountryCode}
                  locale={locale}
                  version={version}
                  option={selectedOption}
                  isValidateErrors={isConsentControlSelected('validate-error')}
                  consentsState={consentsState}
                  customLoader={isConsentControlSelected('show-custom-loader') ? customLoaderInput : null}
                  anchorBehavior={isConsentControlSelected('override-links') ? 'newTab' : 'default'}
                  linkBehavior={termsLinkBehaviour}
                  setConsentsState={setConsentsState}
                  onConsentChange={handleConsentChange}
                  isAuthorMode={isAuthorMode}
                  preState={preState}
                />
              )}

              <div className="mt-4 mb-4" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div
                  className="skeleton-loader"
                  style={{
                    display: 'flex',
                    width: '96px',
                    height: '32px',
                    borderRadius: '20px',
                  }}
                ></div>
                <div
                  className="skeleton-loader mr-4"
                  style={{
                    display: 'flex',
                    width: '96px',
                    height: '32px',
                    borderRadius: '20px',
                  }}
                ></div>
              </div>
            </>
          </OBJConsentsModal>
        )}
      </div>

      {isConsentControlSelected('show-data-inspector') && (
        <DataInspector consentData={consentsState.list} error={consentsState.error} preState={preState} />
      )}
    </form>
  );
};

function ResultPage(props: { consentList: LegalConsent[]; savePreState: (preState: PreState) => void }) {
  const { consentList, savePreState } = props;
  const [isPreSateSaved, setIsPreSateSaved] = useState(false);
  const handleSavePreState = (): void => {
    const updatedPreStateFromConsents: PreState = buildPreStateFromConsents(consentList);
    savePreState(updatedPreStateFromConsents);
    setIsPreSateSaved(true);
  };
  return (
    <div style={{ border: '1px solid blue', marginTop: '30px', borderRadius: '5px' }}>
      <h3>New Component</h3>
      <div>
        <div className="m-3">
          <div
            className="skeleton-loader"
            style={{
              height: '32px',
            }}
          ></div>
        </div>
        <div className="m-3">
          <div
            className="skeleton-loader"
            style={{
              height: '32px',
            }}
          ></div>
        </div>
      </div>
      <Button
        type={ButtonTypeVariation.Button}
        className="m-button  m-3"
        callback={handleSavePreState}
        isDisabled={isPreSateSaved}
      >
        Save as Pre-State
      </Button>
    </div>
  );
}

function ErrorBanner({ consentsState, className }: ErrorBannerProps) {
  const erroredSections = consentsState?.list?.filter(section => section.hasError && section.errorMessage) ?? [];

  if (erroredSections.length === 0) {
    return null;
  }

  return (
    <Messages messageType="error-sev1" className={clsx(className)}>
      <ul>
        {erroredSections.map(section => (
          <li key={section.id}>{section.errorMessage}</li>
        ))}
      </ul>
    </Messages>
  );
}

function DataInspector({
  consentData,
  error,
  preState,
}: {
  consentData: unknown;
  error: ConsentError;
  preState: PreState | undefined;
}) {
  return (
    <div>
      <span>
        <b>Error Code:</b> {error.type}
      </span>
      <div>
        <b>Pre-State:</b>
      </div>
      {!isEmpty(preState) ? <pre>{JSON.stringify(preState, null, 2)}</pre> : <p>No data.</p>}
      <div>
        <b>List:</b>
      </div>
      {consentData ? <pre>{JSON.stringify(consentData, null, 2)}</pre> : <p>No data.</p>}
    </div>
  );
}

function OBJConsentsModal({ children, onModalClose }: { children: React.ReactNode; onModalClose: () => void }) {
  return (
    <Modal
      role="dialog"
      labelledBy="termsModalLabel"
      show={true}
      popupOpenState={true}
      handleBlur={true}
      disableScrollOnBody={true}
      setPopupOpenState={onModalClose}
      onClose={onModalClose}
    >
      <Modal.Header
        labelText={'Consents in the Modal View '}
        customHeadingClass="t-subtitle-xl"
        closeBtnAriaLabel="close-button"
        popupHeaderOnCLoseFunc={onModalClose}
      />
      {/* Invalid vscode error */}
      <Modal.Body className="p-0">{children as JSX.Element}</Modal.Body>
    </Modal>
  );
}
